import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateHelper } from '../../../helpers/injector.helper';
import { NetworkConnection } from '../../../helpers/network.helpers';
import { NotificationHelper } from '../../../helpers/notification.helper';
import { OfflineHelper } from '../../../helpers/offline.helper';
import { MessageType } from '../../../models/enums/messagetype.enum';
import { SyncStatus } from '../../../models/enums/syncstatus.enum';
import { ReceivedMessage } from '../../../models/notification/receivedmessage.model';
import { LanguageService } from '../../../services/language.service';
import { MonacoPreviewDialog } from '../../dialogs/monacopreview/monaco.preview.dialog';

@Component({
    selector: 'evi-messagecenterdialog',
    templateUrl: './messagecenter.dialog.html',
    styleUrls: ['./messagecenter.control.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MessageCenterDialog implements OnInit, OnDestroy {

    MessageTypes = [];
    SelectedMessageType = -1;
    SelectedMessage: MessageCenterMessage;

    OfflinePossible = false;
    Online = false;

    Subscriptions = [];
    SyncDate: any;
    Changes: any;
    AllMessages = [];
    Messages = [];
    DateFormat: Intl.DateTimeFormat;

    constructor(private dialogRef: MatDialogRef<MessageCenterDialog>, @Inject(MAT_DIALOG_DATA) public data: any) {
        this.MessageTypes.push({ Caption: '@@Alle', Value: -1 });
        this.MessageTypes.push({ Caption: '@@Information', Value: MessageType.Information });
        this.MessageTypes.push({ Caption: '@@Exception', Value: MessageType.Error });
        this.MessageTypes.push({ Caption: '@@Warning', Value: MessageType.Warning });
        this.MessageTypes.push({ Caption: '@@Success', Value: MessageType.Success });
        this.SelectedMessageType = this.MessageTypes[0].Value;
        let settings: any = localStorage.getItem("GeneralSettings");
        if (settings) {
            settings = JSON.parse(settings);
        }
        if (settings != null) {
            if (settings.Offline && settings.Offline.Active) {
                this.OfflinePossible = true;
            }
        }
    }
    ngOnInit(): void {
        OfflineHelper.LoadChanges();
        this.Changes = OfflineHelper.Changes;
        this.Subscriptions.push(NetworkConnection.Online.subscribe((online) => {
            this.Online = online;
        }));
        this.Subscriptions.push(LanguageService.SelectedLanguage.subscribe((lang: any) => {
            let locale: string;
            if (lang) {
                locale = lang.Name;
            }
            this.DateFormat = new Intl.DateTimeFormat(locale, {
                weekday: 'long',
                month: 'long',
                day: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
            });
        }));
        this.Subscriptions.push(NotificationHelper.ReceivedMessages.subscribe(x => {
            if (x) {
                this.AllMessages = [...x];
            } else {
                this.AllMessages = [];
            }
            this.filterMessages();
        }));
    }

    ngOnDestroy(): void {
        this.Subscriptions.forEach(x => {
            x.unsubscribe();
        });
    }

    filterMessages() {
        const messages = [];
        if (this.SelectedMessageType < 0) {
            this.AllMessages.forEach(x => {
                messages.push(new MessageCenterMessage(x, this.DateFormat));
            });
        } else {
            this.AllMessages.forEach(x => {
                if (x.MessageType === this.SelectedMessageType) {
                    messages.push(new MessageCenterMessage(x, this.DateFormat));
                }
            });
        }
        this.Messages = messages;
    }

    okClicked() {
        this.dialogRef.close();
    }

    clearClicked() {
        this.SelectedMessage = null;
        NotificationHelper.ClearMessages();
    }

    messageTypeClicked() {
        this.SelectedMessage = null;
        this.filterMessages();
    }

    onItemClick(item: any) {
        if (this.SelectedMessage !== item) {
            this.SelectedMessage = item;
            this.SelectedMessage.setRead();
        }
    }

    copy() {
        if (this.SelectedMessage) {
            const selBox = document.createElement('textarea');
            selBox.style.position = 'fixed';
            selBox.style.left = '0';
            selBox.style.top = '0';
            selBox.style.opacity = '0';
            selBox.value = this.SelectedMessage.getMessageCopyText();
            document.body.appendChild(selBox);
            selBox.focus();
            selBox.select();
            document.execCommand('copy');
            document.body.removeChild(selBox);
        }
    }

    ResetDB() {
        OfflineHelper.DeleteDB().then(() => {
            OfflineHelper.GenerateDB();
            OfflineHelper.StartClientSync(null);
        });
    }
    StartClientSync() {
        OfflineHelper.StartClientSync(new Date(this.SyncDate));
    }
    StartServerSync() {
        OfflineHelper.StartServerSync();
    }
    OpenDocument(item: any) {
        let args = {
            Document: JSON.stringify(item.Item, null, '\t')
        }
        if (item.Status == SyncStatus.Error) {
            args['Original'] = JSON.stringify(item.Original, null, '\t');
        }
        MonacoPreviewDialog.ShowDialog(args, '@@DocumentPreview');
    }

    GetIcon(item: any) {
        let retVal = "";
        switch (item.Status) {
            case SyncStatus.Success: retVal = "sync_success"; break;
            case SyncStatus.Waiting: retVal = "sync_waiting"; break;
            case SyncStatus.Processing: retVal = "sync_processing"; break;
            case SyncStatus.Error: retVal = "sync_error"; break;
        }
        return retVal;
    }
    ItemAction(item: any) {
        if (item.Status == SyncStatus.Waiting) {
            item.Status = SyncStatus.Processing;
            OfflineHelper.SyncSingleDocument(item.ContainerID, item.Item).then((result) => {
                if (result && result['Success']) {
                    item.Status = SyncStatus.Success;
                } else {
                    item.Status = SyncStatus.Error;
                    if (result.Document) {
                        item.Original = JSON.parse(result.Document);
                    }
                }
            })
        }
    }
    TakeOffline(item: any) {
        if (item && item.Item) {
            OfflineHelper.ForceSyncDocument(item, 'Item').then((result) => {
                if (result && result['Success']) {
                    item.Status = SyncStatus.Success;
                } else {
                    item.Status = SyncStatus.Error;
                    if (result.Document) {
                        item.Original = JSON.parse(result.Document);
                    }
                }
            });
        }
    }
    TakeOnline(item: any) {
        if (item && item.Original) {
            OfflineHelper.ForceSyncDocument(item, 'Original').then((result) => {
                if (result && result['Success']) {
                    item.Status = SyncStatus.Success;
                } else {
                    item.Status = SyncStatus.Error;
                    if (result.Document) {
                        item.Original = JSON.parse(result.Document);
                    }
                }
            });
        }
    }
}

export class MessageCenterMessage {

    private static Styles = {
        0: { background: 'rgb(77,174,81)' },
        1: { background: 'rgb(254,152,2)' },
        2: { background: 'rgb(244,67,54)' },
        3: { background: 'rgb(31,135,229)' }
    };

    Title: string;
    CreateDateTextShort: string;
    Content: string;
    Style: any;

    get Read() {
        return this.message.Read;
    }

    constructor(private message: ReceivedMessage, dateFormat: Intl.DateTimeFormat) {
        this.Title = message.Title;
        this.CreateDateTextShort = dateFormat.format(message.CreateDate);
        this.Content = message.Content;
        this.Style = MessageCenterMessage.Styles[message.MessageType];
    }

    setRead() {
        this.message.Read = true;
        NotificationHelper.MessageRead.next();
    }

    getMessageCopyText() {
        const translate = TranslateHelper.TranslatorInstance;
        let text = translate.instant('@@Titel:') + ' ' + this.message.Title + '\n\n';
        text += translate.instant('@@Zeit:') + ' ' + this.message.CreateDate.toString() + '\n\n';
        text += translate.instant('@@Nachricht:') + ' ' + this.message.Content;
        return text;
    }
}
